/** Evidence-quality signal: has a GPS location been captured for this
 * inspection yet? (GPS robustness #4.)
 *
 * The field's device is the authoritative source of location — but on a
 * cold/denied GPS it may never post one. This badge lets the operator SEE
 * that during the live session, so they can prompt the field user to enable
 * location instead of discovering an empty geo only on the finished report.
 *
 * "Captured" means `session.geo_jsonb` carries real coordinates. Polled
 * every 15s (cheap, admin-facing) so it flips to green the moment the field
 * posts a fix — from join, an in-room retry, or any capture. Only shown
 * while the session is active; a finished inspection's geo is what it is.
 */
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { ApiPaths, api } from "@/lib/api";
import type { Session } from "@/lib/types";

type Props = {
  sessionId: string;
  /** Hide the badge entirely once the session is terminal/scheduled — a
   * live "not captured" prompt is only actionable during the session. */
  active: boolean;
};

function hasCoords(geo: Record<string, unknown> | null | undefined): boolean {
  if (!geo) return false;
  const lat = geo.lat;
  const lng = geo.lng ?? geo.lon;
  return typeof lat === "number" && typeof lng === "number";
}

export function GpsStatusBadge({ sessionId, active }: Props) {
  const { t } = useTranslation();
  const session = useQuery({
    queryKey: ["session-geo", sessionId],
    queryFn: async () =>
      (await api.get<Session>(ApiPaths.session(sessionId))).data,
    refetchInterval: active ? 15_000 : false,
    enabled: active,
  });

  if (!active) return null;

  const captured = hasCoords(
    session.data?.geo_jsonb as Record<string, unknown> | undefined,
  );

  const tone = captured
    ? "border-nb-mint bg-nb-mint/10 text-nb-mint"
    : "border-amber-500 bg-amber-500/10 text-amber-200";

  return (
    <span
      data-testid="gps-status-badge"
      className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${tone}`}
      title={captured ? undefined : t("ui.gpsStatusBadge.tooltip")}
    >
      {captured
        ? t("ui.gpsStatusBadge.captured")
        : t("ui.gpsStatusBadge.missing")}
    </span>
  );
}
